import { Select } from "@mantine/core";
import { t } from "i18next";
import type { MRT_ColumnDef } from "mantine-react-table-open";
import { setErrorsFromResponseData } from "../../../../components/BuildingBlocks/Forms/utils/setErrorsFromResponseData";
import { LoadOrError } from "../../../../components/LoadOrError/LoadOrError";
import { useMeteringOrMarketLocations } from "../../../../hooks/useMeteringOrMarketLocations";
import { useSuggestedMeteringOrMarketLocation } from "../../../../hooks/useSuggestedMeteringOrMarketLocation";
import { useToast } from "../../../../mantine/notifications/useToast";
import type {
  ExpressOnboardingGenerator,
  GetErrorFn,
  SetErrorFn
} from "../../ExpressOnboarding.types";
import { useExpressOnboardingMutations } from "../hooks/useExpressOnboardingMutations";

function getExpressOnboardingTableLocationIdColumn(
  getError: GetErrorFn,
  setError: SetErrorFn
): MRT_ColumnDef<ExpressOnboardingGenerator> {
  return {
    id: "location_id",
    accessorKey: "location_id",
    header: "Zählpunkt",
    enableGlobalFilter: false,
    size: 300,
    Cell: ({ cell, row }) => {
      const value = cell.getValue<ExpressOnboardingGenerator["location_id"]>();
      const siteId = cell.row.original.site_id;
      const generatorId = cell.row.original.id;

      const {
        data: meteringOrMarketLocations,
        isLoading: isLoadingMeteringOrMarketLocations,
        isError: isErrorMeteringOrMarketLocations,
        error: meteringOrMarketLocationsError
      } = useMeteringOrMarketLocations({ siteId });
      const {
        suggestedMeteringOrMarketLocation,
        isLoading: isLoadingSuggestedMeteringOrMarketLocation
      } = useSuggestedMeteringOrMarketLocation(generatorId);

      const { expressOnboardingGeneratorSave, pendingGeneratorIds } =
        useExpressOnboardingMutations();

      const { showErrorToast } = useToast();

      const rowIsSaving = pendingGeneratorIds.has(row.original.id);

      return (
        <LoadOrError
          error={
            isErrorMeteringOrMarketLocations
              ? meteringOrMarketLocationsError
              : undefined
          }
        >
          <Select
            data={
              meteringOrMarketLocations?.map((location) => ({
                value: location.id,
                label: `${location.name}${suggestedMeteringOrMarketLocation?.id === location.id ? " (automatischer Vorschlag)" : ""}`
              })) ?? []
            }
            defaultValue={value}
            disabled={rowIsSaving}
            error={getError(row.original.id, "location_id")}
            loading={
              isLoadingMeteringOrMarketLocations ||
              isLoadingSuggestedMeteringOrMarketLocation ||
              rowIsSaving
            }
            onChange={(newValue) => {
              const hasChange = newValue !== value;
              if (!hasChange) {
                return;
              }

              expressOnboardingGeneratorSave
                .mutateAsync({
                  id: row.original.id,
                  location_id: newValue,
                  generation_share:
                    row.original.generation_share === null ? 0 : undefined
                })
                .catch((error) => {
                  setErrorsFromResponseData(
                    showErrorToast,
                    error,
                    row.original,
                    (field: string, err: { message?: string }) =>
                      setError(row.original.id, {
                        [field]: err?.message ?? t("errors.UnknownError")
                      }),
                    t("errors.UnknownError")
                  );
                });
            }}
          />
        </LoadOrError>
      );
    }
  };
}

export { getExpressOnboardingTableLocationIdColumn };
